/**
 * PremisesStepper
 *
 * Four-step navigation for premises liability workflow.
 * Steps: Review Incident Properties -> Research Property & Owners
 * -> Write a Letter of Representation -> Review & Send LOR
 *
 * - First 2 steps always navigate to their dedicated pages
 * - Claims-review steps use onGoto callback (for URL param updates when on ClaimFilingPage)
 * - Checkmarks show task completion based on premises-specific tasks
 * - Write a Letter of Representation: done when SEND_LOR is under review OR completed
 * - Review & Send LOR: done ONLY when SEND_LOR is completed
 */
import { Check } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  buildClaimsFilingUrl,
} from '@/components/claims-filing/buildClaimsFilingUrl';
import {
  buildPremisesFollowUpUrlForType,
  getPremisesFollowUpLabelForType,
  getPremisesFollowUpStepKeyForType,
} from '@/components/claims-filing/premises/premisesFlow';
import { useCoverageStepProgress } from '@/components/claims-filing/state/useCoverageStepProgress';
import { cn } from '@/lib/utils';
import { useAllSubjectsWithRelationshipsQuery } from '@/services/api/subjectsService';
import { CoverageSchema } from '@/services/types/coverage-types';

export type PremisesStep =
  | 'confirm_location'
  | 'research_property'
  | 'lor'
  | 'review_send'
  | 'open_premises_claim'
  | 'research_alternate_contacts';

interface PremisesStepperProps {
  matterId: string;
  coverageId: string;
  currentStep: PremisesStep;
  coverage?: CoverageSchema;
  /** Callback for LOR steps (lor, review_send) - first 2 steps always navigate */
  onGoto?: (step: PremisesStep) => void;
}

const StepPill: React.FC<{
  label: string;
  active: boolean;
  done?: boolean;
  na?: boolean;
  onClick?: () => void;
}> = ({ label, active, done, na, onClick }) => (
  <button
    type="button"
    className={cn(
      'relative px-2 py-2 text-sm font-medium transition-colors',
      active ? 'text-violet-600' : 'text-zinc-800 hover:text-zinc-900',
    )}
    onClick={onClick}
    aria-current={active ? 'step' : undefined}
  >
    <span className="inline-flex items-center gap-2">
      <span>{label}</span>
      {done ? (
        <span className="inline-flex items-center gap-1">
          {na && (
            <span className="text-[10px] font-medium bg-gray-100 text-gray-700 border border-gray-200 rounded px-1.5 py-0.5">
              NA
            </span>
          )}
          <span className="h-5 w-5 rounded-full bg-green-600 text-white flex items-center justify-center">
            <Check className="h-3 w-3" />
          </span>
        </span>
      ) : (
        <span className="h-5 w-5 rounded-full border border-zinc-300 bg-white" />
      )}
    </span>
    {active && (
      <span className="absolute left-0 right-0 -bottom-0.5 h-0.5 bg-violet-600 rounded" />
    )}
  </button>
);

export const PremisesStepper: React.FC<PremisesStepperProps> = ({
  matterId,
  coverageId,
  currentStep,
  coverage,
  onGoto,
}) => {
  const navigate = useNavigate();

  // Get step progress from the unified hook
  const {
    confirmLocationDone,
    confirmLocationNA,
    researchPropertyOwnersDone,
    researchPropertyOwnersNA,
    sendLorPremisesDone,
    sendLorPremisesNA,
    reviewSendPremisesDone,
    premisesFollowUpDone,
    premisesFollowUpNA,
    premisesFollowUpTaskType,
  } = useCoverageStepProgress(matterId, coverageId);
  const { data: subjectsData } = useAllSubjectsWithRelationshipsQuery(
    { matterId },
    { skip: !matterId || !coverage?.property_id },
  );

  const propertyAddress = React.useMemo(() => {
    const propertyId = coverage?.property_id;
    if (!propertyId) {
      return null;
    }
    const linkedProperty = subjectsData?.properties?.find(
      (property) => property.id === propertyId,
    );
    const physicalAddress = linkedProperty?.physical_address;
    if (!physicalAddress) {
      return null;
    }
    const cityStateZip = [physicalAddress.city, physicalAddress.state, physicalAddress.zip]
      .filter(Boolean)
      .join(', ');
    const parts = [
      physicalAddress.line1,
      physicalAddress.line2,
      cityStateZip,
    ].filter(Boolean);
    return parts.join(', ') || null;
  }, [coverage?.property_id, subjectsData?.properties]);

  const coverageLabel = React.useMemo(() => {
    if (propertyAddress) {
      return propertyAddress;
    }
    if (coverage?.named_insured && coverage.named_insured.length > 0) {
      return coverage.named_insured[0];
    }
    return coverage?.carrier?.name || 'Property';
  }, [coverage, propertyAddress]);

  // Navigation handlers - first 2 steps ALWAYS navigate to their pages
  const handleConfirmLocation = () => {
    navigate(`/matters/${matterId}/premises/${coverageId}/confirm-location`);
  };

  const handleResearchProperty = () => {
    navigate(`/matters/${matterId}/premises/${coverageId}/research-property`);
  };

  // Claims-review steps use onGoto if provided, otherwise navigate
  const handleWriteLor = () => {
    if (onGoto) {
      onGoto('lor');
    } else {
      navigate(buildClaimsFilingUrl(matterId, coverageId, 'lor'));
    }
  };

  const handleReviewSend = () => {
    if (onGoto) {
      onGoto('review_send');
    } else {
      navigate(buildClaimsFilingUrl(matterId, coverageId, 'review_send'));
    }
  };

  const followUpLabel = getPremisesFollowUpLabelForType(premisesFollowUpTaskType);
  const followUpHref = buildPremisesFollowUpUrlForType(
    matterId,
    coverageId,
    premisesFollowUpTaskType,
  );
  const followUpStepKey = getPremisesFollowUpStepKeyForType(
    premisesFollowUpTaskType,
  );

  return (
    <div className="w-full border-b bg-white">
      <div className="mx-auto max-w-7xl px-4 py-3 flex items-start justify-between gap-6">
        <div className="min-w-0 max-w-[18rem] text-sm font-semibold leading-5 text-zinc-900 whitespace-normal break-words">
          {coverageLabel} • Premises Liability
        </div>
        <div className="flex flex-1 items-center justify-end gap-10">
          <StepPill
            label="Review Incident Properties"
            active={currentStep === 'confirm_location'}
            done={confirmLocationDone}
            na={confirmLocationNA}
            onClick={handleConfirmLocation}
          />
          <StepPill
            label="Research Property & Owners"
            active={currentStep === 'research_property'}
            done={researchPropertyOwnersDone}
            na={researchPropertyOwnersNA}
            onClick={handleResearchProperty}
          />
          <StepPill
            label="Write a Letter of Representation"
            active={currentStep === 'lor'}
            done={sendLorPremisesDone}
            na={sendLorPremisesNA}
            onClick={handleWriteLor}
          />
          <StepPill
            label="Review & Send LOR"
            active={currentStep === 'review_send'}
            done={reviewSendPremisesDone}
            onClick={handleReviewSend}
          />
          {followUpLabel && followUpHref && (
            <StepPill
              label={followUpLabel}
              active={followUpStepKey === currentStep}
              done={premisesFollowUpDone}
              na={premisesFollowUpNA}
              onClick={() => navigate(followUpHref)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
